// The booking draft and its step machine.
//
// This is OUR state, not the engine's (plan §4.1, "replay, don't resume").
// Nothing here knows what a booking engine is: the draft is a declarative
// statement of what the guest wants, and it is the only thing that has to
// survive a reconnect, a driver swap, or the eventual native engine.
//
// Keeping it separate from SliderState is deliberate · the slider is a
// presentation stack, this is a transaction. They have different lifetimes.

import { create } from "zustand";
import Analytics from "../../../../../utils/Analytics";
import mockBookingDriver, { BookingDriver } from "./bookingDriver";

/**
 * Whether the booking flow is reachable at all.
 *
 * There is exactly ONE switch, and it is the backoffice's: the per-assistant
 * toggle under Assistant details → Integrazioni. The backend reads it into the
 * chat configuration as `isBookingEnabled`, and the store below holds whatever
 * it said. Nothing here re-derives the rule · a widget that disagreed with the
 * backoffice about whether booking is on is exactly the bug worth designing
 * out, and a second switch in the bundle is how that bug gets built.
 *
 * The dev override is not a second switch: it is compiled out of production
 * builds by the NODE_ENV check, and exists so the flow can be driven locally
 * against a backend that hasn't shipped the field yet.
 */
const devOverride = () =>
  process.env.NODE_ENV === "development" &&
  (globalThis as any).__bookingEnabled === true;

export const isBookingEnabled = (): boolean =>
  useBookingState.getState().enabled || devOverride();

/** Reactive form of {@link isBookingEnabled}, for use inside components. */
export const useBookingEnabled = (): boolean => {
  const enabled = useBookingState((s) => s.enabled);
  return enabled || devOverride();
};

export type BookingStep =
  | "rate"
  | "details"
  | "review"
  | "submitting"
  | "success"
  | "error";

export type DraftRate = {
  id: string | null;
  title: string;
  price: number;
  // Guest-facing line from dengine, carried through so the summary can keep
  // showing what the price includes after the picker is gone.
  conditions?: string;
};

export type DraftStay = {
  checkin?: string | null;
  checkout?: string | null;
  nights?: number | null;
  adults?: number | null;
  children?: number | null;
};

export type DraftGuest = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
};

export type BookingDraft = {
  id: string;
  roomId: string | null;
  roomTitle: string;
  imageUrl: string | null;
  rates: DraftRate[];
  rate: DraftRate | null;
  // Position of `rate` in `rates`. Two group rates can share a title, so the
  // picker highlights by position instead of matching the label.
  rateIndex: number | null;
  stay: DraftStay;
  guest: DraftGuest;
  consent: { privacy: boolean; marketing: boolean };
};

export type GuestField = keyof DraftGuest;

const emptyGuest = (): DraftGuest => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  notes: "",
});

/**
 * Seed the form with what the conversation already knows. The guest gave a
 * name and a phone to GET the quote; asking again is a fee we charge them for
 * having cooperated.
 *
 * The name arrives as one string, so the split is a guess · first token is the
 * first name, the rest the surname. It is a prefill, not a verdict: every field
 * stays editable, and a single-word name leaves the surname empty rather than
 * putting the same word in both.
 */
const prefilledGuest = (name?: string | null, phone?: string | null): DraftGuest => {
  const guest = emptyGuest();
  guest.phone = phone?.trim() ?? "";

  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    guest.firstName = parts[0];
  } else if (parts.length > 1) {
    guest.firstName = parts[0];
    guest.lastName = parts.slice(1).join(" ");
  }
  return guest;
};

const newId = () => {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `d-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
};

// Deliberately permissive: a widget is not a border control, and a rejected
// guest is worth more than a perfectly shaped record. We check the shape that
// makes the field usable at all, nothing beyond it.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+]?[\d\s().-]{6,}$/;

export type FieldErrors = Partial<Record<GuestField, true>>;

export const validateGuest = (guest: DraftGuest): FieldErrors => {
  const errors: FieldErrors = {};
  if (!guest.firstName.trim()) errors.firstName = true;
  if (!guest.lastName.trim()) errors.lastName = true;
  if (!EMAIL_RE.test(guest.email.trim())) errors.email = true;
  if (!PHONE_RE.test(guest.phone.trim())) errors.phone = true;
  return errors;
};

type StartArgs = {
  roomId?: string | null;
  roomTitle: string;
  imageUrl?: string | null;
  rates: DraftRate[];
  rate?: DraftRate | null;
  rateIndex?: number | null;
  stay?: DraftStay;
  /** Whatever the conversation already established about the guest. */
  guestName?: string | null;
  guestPhone?: string | null;
};

type BookingStoreState = {
  /**
   * The backend's verdict: the `booking` capability AND the per-assistant
   * toggle. Pushed in by Chat when the chat configuration lands; false until
   * then, so a slow config fetch can never flash the booking CTA.
   */
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;

  isActive: boolean;
  step: BookingStep;
  draft: BookingDraft | null;
  errors: FieldErrors;
  showErrors: boolean;
  reference: string | null;
  failure: string | null;
  driver: BookingDriver;

  start: (args: StartArgs) => void;
  cancel: () => void;
  chooseRate: (rate: DraftRate, index?: number) => void;
  setGuestField: (field: GuestField, value: string) => void;
  setConsent: (key: "privacy" | "marketing", value: boolean) => void;
  goToDetails: () => void;
  goToReview: () => void;
  back: () => void;
  submit: () => Promise<void>;
  setDriver: (driver: BookingDriver) => void;
};

export const useBookingState = create<BookingStoreState>((set, get) => ({
  enabled: false,
  setEnabled: (enabled: boolean) => set({ enabled }),

  isActive: false,
  step: "rate",
  draft: null,
  errors: {},
  showErrors: false,
  reference: null,
  failure: null,
  driver: mockBookingDriver,

  start: ({ roomId, roomTitle, imageUrl, rates, rate, rateIndex, stay, guestName, guestPhone }) => {
    // One rate is not a choice · skip straight to the part that needs a human.
    const only = rates.length === 1 ? rates[0] : null;
    const indexed =
      rateIndex !== undefined && rateIndex !== null
        ? rates[rateIndex] ?? null
        : null;
    const chosen = rate ?? indexed ?? only ?? null;
    const position = chosen ? rates.indexOf(chosen) : -1;
    const guest = prefilledGuest(guestName, guestPhone);
    const prefilled = Object.entries(guest)
      .filter(([, v]) => v)
      .map(([k]) => k);

    Analytics.track("booking_started", {
      room_title: roomTitle,
      room_id: roomId ?? null,
      rate_title: chosen?.title ?? null,
      rate_count: rates.length,
      // How much of the form we could fill for them · the number to watch if
      // completion moves.
      prefilled_fields: prefilled,
    });

    set({
      isActive: true,
      step: chosen ? "details" : "rate",
      errors: {},
      showErrors: false,
      reference: null,
      failure: null,
      draft: {
        id: newId(),
        roomId: roomId ?? null,
        roomTitle,
        imageUrl: imageUrl ?? null,
        rates,
        rate: chosen,
        rateIndex: position >= 0 ? position : null,
        stay: stay ?? {},
        guest,
        consent: { privacy: false, marketing: false },
      },
    });
  },

  cancel: () => {
    const { step, draft } = get();
    // Only worth an event if they were mid-flow; closing the success screen is
    // not an abandonment.
    if (draft && step !== "success") {
      Analytics.track("booking_abandoned", {
        step,
        room_title: draft.roomTitle,
      });
    }
    set({
      isActive: false,
      step: "rate",
      draft: null,
      errors: {},
      showErrors: false,
      reference: null,
      failure: null,
    });
  },

  chooseRate: (rate, index) => {
    const draft = get().draft;
    if (!draft) return;
    const position = index ?? draft.rates.indexOf(rate);
    Analytics.track("room_selected", {
      room_title: draft.roomTitle,
      room_id: draft.roomId,
      rate_title: rate.title,
      rate_id: rate.id,
      price: rate.price,
    });
    set({
      draft: {
        ...draft,
        rate,
        rateIndex: position >= 0 ? position : null,
      },
      step: "details",
    });
  },

  setGuestField: (field, value) => {
    const draft = get().draft;
    if (!draft) return;
    const guest = { ...draft.guest, [field]: value };
    set({
      draft: { ...draft, guest },
      // Re-validate live only once they've been told something is wrong,
      // so the form doesn't scold anyone mid-typing.
      errors: get().showErrors ? validateGuest(guest) : get().errors,
    });
  },

  setConsent: (key, value) => {
    const draft = get().draft;
    if (!draft) return;
    set({ draft: { ...draft, consent: { ...draft.consent, [key]: value } } });
  },

  goToDetails: () => set({ step: "details" }),

  goToReview: () => {
    const draft = get().draft;
    if (!draft) return;
    const errors = validateGuest(draft.guest);
    if (Object.keys(errors).length) {
      set({ errors, showErrors: true });
      return;
    }
    Analytics.track("guest_details_submitted", {
      room_title: draft.roomTitle,
      rate_title: draft.rate?.title ?? null,
    });
    set({ errors: {}, showErrors: false, step: "review" });
  },

  back: () => {
    const { step, draft } = get();
    if (step === "review") return set({ step: "details" });
    if (step === "details" && (draft?.rates.length ?? 0) > 1)
      return set({ step: "rate" });
    get().cancel();
  },

  submit: async () => {
    const { draft, driver, step } = get();
    // Idempotence starts here: the button is disabled while submitting, but a
    // stray Enter or a double tap must not reach the driver twice.
    if (!draft || step === "submitting") return;

    set({ step: "submitting", failure: null });

    Analytics.track("booking_submitted", {
      draft_id: draft.id,
      room_title: draft.roomTitle,
      rate_title: draft.rate?.title ?? null,
      price: draft.rate?.price ?? null,
    });

    try {
      const result = await driver.submit(draft);
      if (result.status === "confirmed") {
        Analytics.track("booking_confirmed", {
          draft_id: draft.id,
          reference: result.reference,
        });
        set({ step: "success", reference: result.reference });
      } else {
        Analytics.track("booking_failed", {
          draft_id: draft.id,
          reason: result.reason,
        });
        set({ step: "error", failure: result.reason });
      }
    } catch (error: any) {
      Analytics.track("booking_failed", {
        draft_id: draft.id,
        reason: error?.message ?? "unknown",
      });
      set({ step: "error", failure: error?.message ?? "unknown" });
    }
  },

  setDriver: (driver) => set({ driver }),
}));

export default useBookingState;
